import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import type { PortalOverviewDTO, TenantPaymentAttemptDTO } from '@propmgt/shared';
import { api, errorMessage } from '../../api/client';
import { describeBalance, formatCents } from '../../lib/money';
import { formatDate } from '../../lib/date';
import { ErrorState, Loading } from '../../components/ui/Feedback';
import { TenantPaymentActivityList } from '../../components/payments/PaymentActivity';

/** Attempts that need the tenant's attention or are in flight (shown on the dashboard). */
const ATTENTION = ['processing', 'requires_action', 'created', 'requires_payment_method', 'requires_confirmation', 'payment_failed'];

/** Pure presentational tenant dashboard (T9.1). Renders from a PortalOverviewDTO fixture or live. */
export function DashboardView({
  data,
  paymentAttempts = [],
}: {
  data: PortalOverviewDTO;
  /** 0.26.0 — the tenant's own online payment attempts (optional; fetched by the container). */
  paymentAttempts?: TenantPaymentAttemptDTO[];
}) {
  const balance = describeBalance(data.balanceCents);
  const attention = paymentAttempts.filter((a) => ATTENTION.includes(a.status));
  return (
    <>
      <h1>Welcome, {data.tenantName}</h1>
      <p className="subtitle">Here is a summary of your tenancy.</p>

      {!data.lease ? (
        <div className="card">
          <h2>No active lease</h2>
          <p>
            You do not have an active lease on file right now. If you believe this is a mistake,
            please <Link to="/portal/messages">message your property manager</Link>.
          </p>
        </div>
      ) : (
        <>
          <section aria-labelledby="summary-heading">
            <h2 id="summary-heading">At a glance</h2>
            <div className="grid">
              <div className="stat">
                <p className="stat-label">Account balance</p>
                <p className="stat-value">{balance.amount}</p>
                <p className="muted" style={{ margin: 0 }}>
                  {balance.label}
                </p>
                {(data.pendingPaymentCents ?? 0) > 0 && (
                  <p className="muted" style={{ margin: '0.25rem 0 0' }}>
                    Pending: {formatCents(data.pendingPaymentCents ?? 0)} bank payment processing (not yet applied)
                  </p>
                )}
              </div>
              <div className="stat">
                <p className="stat-label">Monthly rent</p>
                <p className="stat-value">{formatCents(data.lease.rentCents)}</p>
              </div>
              <div className="stat">
                <p className="stat-label">Open maintenance</p>
                <p className="stat-value">{data.openWorkOrderCount}</p>
                <p className="muted" style={{ margin: 0 }}>
                  <Link to="/portal/maintenance">View requests</Link>
                </p>
              </div>
              <div className="stat">
                <p className="stat-label">Unread messages</p>
                <p className="stat-value">{data.unreadMessageCount}</p>
                <p className="muted" style={{ margin: 0 }}>
                  <Link to="/portal/messages">Open messages</Link>
                </p>
              </div>
            </div>
          </section>

          {attention.length > 0 && (
            <section aria-labelledby="online-pay-heading">
              <h2 id="online-pay-heading">Your online payments</h2>
              <TenantPaymentActivityList attempts={attention} limit={3} />
              <p>
                <Link to="/portal/pay">Go to Pay rent</Link>
              </p>
            </section>
          )}

          <section aria-labelledby="home-heading">
            <h2 id="home-heading">Your home</h2>
            <div className="card">
              <p style={{ margin: '0 0 0.25rem' }}>
                <strong>{data.lease.propertyName ?? 'Your unit'}</strong>
                {data.lease.unitLabel ? ` — Unit ${data.lease.unitLabel}` : ''}
              </p>
              {data.lease.propertyAddress && (
                <p className="muted" style={{ margin: 0 }}>
                  {data.lease.propertyAddress}
                </p>
              )}
              <p style={{ marginTop: '0.5rem' }}>
                Lease term: {formatDate(data.lease.startDate)} –{' '}
                {data.lease.endDate ? formatDate(data.lease.endDate) : 'month-to-month'}{' '}
                <span className="badge">{data.lease.status}</span>
              </p>
              <p>
                <Link to="/portal/lease">View lease details</Link>
              </p>
            </div>
          </section>

          <section aria-labelledby="upcoming-heading">
            <h2 id="upcoming-heading">Upcoming payments</h2>
            {data.nextInstallments.length === 0 ? (
              <p className="muted">No upcoming installments are scheduled.</p>
            ) : (
              <div className="table-wrap card" style={{ padding: 0 }}>
                <table>
                  <caption className="visually-hidden">Upcoming rent installments</caption>
                  <thead>
                    <tr>
                      <th scope="col">Installment</th>
                      <th scope="col">Next due</th>
                      <th scope="col" className="num">
                        Amount
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.nextInstallments.map((inst, i) => (
                      <tr key={`${inst.dayOfMonth}-${i}`}>
                        <td>{inst.label ?? `Day ${inst.dayOfMonth}`}</td>
                        <td>{formatDate(inst.nextDueDate)}</td>
                        <td className="num">{formatCents(inst.amountCents)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p>
              <Link to="/portal/payment-methods">See ways to pay</Link>
            </p>
          </section>
        </>
      )}
    </>
  );
}

/** Container: fetches the dashboard overview and renders the view. */
export function Dashboard() {
  const query = useQuery({
    queryKey: ['portal', 'overview'],
    queryFn: () => api.portal.overview(),
  });

  const attempts = useQuery({
    queryKey: ['portal', 'payment-attempts'],
    queryFn: () => api.portal.paymentAttempts.list(),
  });

  if (query.isLoading) return <Loading label="Loading your dashboard…" />;
  if (query.isError || !query.data)
    return <ErrorState message={errorMessage(query.error)} onRetry={() => query.refetch()} />;
  return <DashboardView data={query.data} paymentAttempts={attempts.data?.attempts ?? []} />;
}
